/**
 * Audience-input feature: combines upvoting, multi-answer composer, and an
 * inline poll question to gather lightweight audience signal on a question.
 *
 * Bundles three previously-separate behaviors into a single feature for the
 * Question structure: an upvote action on each comment (reading the shared
 * `comment.upvoteCount` tally, the viewer's own state from `useMyUpvotes()`,
 * and writing via `setUpvoteAsync`/`removeUpvoteAsync` from `data/comment`),
 * the multi-answer
 * composer (each user can post more than one response), and a "poll question"
 * that author/editorial can configure to surface a single multiple-choice
 * prompt above the comments. The poll's question and options are read and
 * written through the `spaces.pollOptions` resource (`data/poll-options`), and
 * a visitor's vote through `poll-votes` (`data/poll-votes`), with its tally
 * surfaced as the `poll` breakdown on `comments.stats`.
 */

import type { Comment } from '@repo/shared/orpc/procedures/comments';
import { useTheme } from '@repo/theme';
import { Pad, PadBox, Separator } from 'component/basics';
import { SubtleButton } from 'component/button';
import type { CommentConfig } from 'component/comment';
import { CommentsInput } from 'component/comment';
import { ListSelector } from 'component/form';
import { SaveableTextField } from 'component/form/saveable-text-field';
import { IconUpvote, IconUpvoted } from 'component/icon';
import { PollWidget } from 'component/poll';
import { TextField } from 'component/text';
import { Tooltip } from 'component/tooltip';
import { removeUpvoteAsync, setUpvoteAsync, useMyUpvotes } from 'data/comment';
import { setSpacePollOptionsAsync, useSpacePollOptions } from 'data/poll-options';
import { useIsReadOnly } from 'data/spaces';
import { useState } from 'react';
import { View } from 'react-native';
import type { QuestionConfig } from 'structure/question';
import { trackEvent } from 'util/analytics';
import { useDatastore, usePersonaKey } from 'util/datastore';
import type { Feature } from 'util/stdtypes';
import { t } from 'util/stdtypes';

// --- Feature Definition ---

const PAGE_SIZE = 5;

export const AudienceInputFeature: Feature<QuestionConfig, CommentConfig> = {
    name: 'Audience Input',
    // eslint-disable-next-line @repo/psi/feature-structure-key-case
    key: 'audience_input',
    showToggle: false,
    providesConfig: {
        // From upvote
        commentActions: [ActionUpvote],
        // From multi-answer
        composerPreview: CommentsInput,
        commentAllowMultiple: true,
        // From poll-question-user-input
        betweenCommentsWidgets: [PollQuestionUserInput],
        editScreenWidgets: [
            {
                slot: 'prosocial-format-widget',
                component: PollQuestionEditScreenWidget,
            },
        ],
        commentPageSize: PAGE_SIZE,
    },
};

// --- Upvote ---

export type ActionUpvoteProps = { comment: Comment; depth?: number };

export function ActionUpvote({ comment }: ActionUpvoteProps) {
    const personaKey = usePersonaKey();
    const { colors } = useTheme();
    const datastore = useDatastore();
    const readOnly = useIsReadOnly();
    const count = comment.upvoteCount ?? 0;
    const upvoted = useMyUpvotes().has(comment.commentKey);
    const alwaysDisabled = comment.authorKey === personaKey || readOnly;
    const [isDisabled, setIsDisabled] = useState(false);

    async function onUpvote() {
        setIsDisabled(true);
        if (upvoted) {
            trackEvent(datastore, 'upvote-undo', { commentKey: comment.commentKey });
            await removeUpvoteAsync(datastore, { commentKey: comment.commentKey });
        } else {
            await setUpvoteAsync(datastore, { commentKey: comment.commentKey });
            trackEvent(datastore, 'upvote', { commentKey: comment.commentKey });
        }
        setIsDisabled(false);
    }

    return (
        <Tooltip tooltipLabel={t('upvote.aria_label', 'Upvote')}>
            <SubtleButton
                ariaLabel={t('upvote.aria_label', 'Upvote')}
                color={upvoted ? colors.colorTextPrimary : colors.colorTextSecondary}
                disabled={alwaysDisabled || isDisabled}
                formatParams={{ count }}
                icon={upvoted ? IconUpvoted : IconUpvote}
                label={t('upvote.count', '{count}')}
                onPress={datastore.needsLogin(onUpvote, 'upvote')}
                testID='upvote'
            />
        </Tooltip>
    );
}

// --- Poll Question ---

export function PollQuestionUserInput({ withTopSeparator }: { withTopSeparator?: boolean }) {
    const { poll } = useSpacePollOptions();
    if (!poll.question || !poll.options.length) return null;

    return (
        <>
            {withTopSeparator && <Separator />}
            <PadBox vert={20}>
                <PollWidget optionTexts={poll.options} question={poll.question} />
            </PadBox>
        </>
    );
}

// Each editor here replaces the whole poll, reading the parts it isn't
// changing back from `poll` — the endpoint takes it wholesale.
function PollQuestionEditScreenWidget() {
    const { poll } = useSpacePollOptions();
    const datastore = useDatastore();

    async function onSaveQuestion(question: string) {
        await setSpacePollOptionsAsync(datastore, { ...poll, question });
    }

    async function onAddOptions(options: string[]) {
        await setSpacePollOptionsAsync(datastore, { ...poll, options: [...poll.options, ...options] });
    }

    async function onDeleteOption(option: string) {
        await setSpacePollOptionsAsync(datastore, { ...poll, options: poll.options.filter((o) => o !== option) });
    }

    async function onChangeSortOrder(orderedKeys: string[]) {
        await setSpacePollOptionsAsync(datastore, { ...poll, options: orderedKeys });
    }

    const items = poll.options.map((option) => ({ key: option, text: option }));

    return (
        <View>
            <SaveableTextField
                label={t('poll_question.question_label', 'Poll question (optional)')}
                onSave={onSaveQuestion}
                placeholder={t('poll_question.question_placeholder', 'Write your poll question...')}
                testID='poll-question-input'
                value={poll.question}
            />

            <Pad size={16} />

            <ListSelector
                addLabel={t('poll_question.add_option_button', 'Add option')}
                addWidget={AddOption}
                items={items}
                label={t('poll_question.options_label', 'Poll options')}
                onAddItems={onAddOptions}
                onChangeSortOrder={onChangeSortOrder}
                onDeleteItem={onDeleteOption}
                reorderable
            />
        </View>
    );
}

type AddOptionProps = {
    setToAdd: (items: string[]) => void;
    onSubmit?: () => void;
};

function AddOption({ setToAdd, onSubmit }: AddOptionProps) {
    const [value, setValue] = useState('');
    return (
        <TextField
            autoFocus={true}
            multiline={false}
            onChange={(text) => {
                setValue(text);
                setToAdd([text]);
            }}
            onSubmit={onSubmit}
            placeholderLabel={t('poll_question.option_placeholder', 'Write your option...')}
            testID='poll-option-input'
            value={value}
        />
    );
}
